import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { DimensionamentoService } from "../../services/DimensionamentoServices";
import { Tooltip } from "react-tooltip";
import {Tabs, Tab, useMediaQuery, Select, MenuItem, InputLabel, FormControl, Box, IconButton} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TableUF from "./TableUF";
import TableChartIcon from "@mui/icons-material/TableChart";

const ufs = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO"
];

const categorias = {
  "Profissional de Saúde": [
    "Cadastro Médicos 4 esp.",
    "Utilização Médicos 4 esp.",
    "Cadastro Médico cirurgião geral",
    "Utilização Médico cirurgião geral",
    "Cadastro Médico clínico geral",
    "Utilização Médico clínico geral",
    "Cadastro Médico gineco obst.",
    "Utilização Médico gineco obst.",
    "Cadastro Médico pediatra",
    "Utilização Médico pediatra",
  ],
  "Urgência e Emergência": [
    "Cadastro Estabelecimentos Urgência",
    "Contratação Estabelecimentos Urgência",
    "Interseção Vazios Urgência",
    "Utilização Estabelecimento Urgência",
  ],
  "Internação": [
    "Cadastro Estabelecimentos Internação",
    "Contratação Estabelecimentos Internação",
    "Interseção Vazios Internação",
    "Utilização Estabelecimento Internação",
  ],
  "Serviço Alta Complexidade": [
    "Cadastro Serviço Quimioterapia",
    "Cadastro Serviço Radioterapia",
    "Cadastro Serviço Hemodiálise",
    "Utilização Serviço Quimioterapia",
    "Utilização Serviço Radioterapia",
    "Utilização Serviço Hemodiálise",
    "Interseção dos vazios Alta Complexidade",
    "Contratação Serviço Alta Complexidade",
  ],
};

const getDisponibilidadeIndex = (especialidade) => {
  const mapaIndices = {
    "Cadastro Médicos 4 esp.": 20,
    "Cadastro Médico cirurgião geral": 21,
    "Cadastro Médico clínico geral": 22,
    "Cadastro Médico gineco obst.": 23,
    "Cadastro Médico pediatra": 24,
    "Utilização Médicos 4 esp.": 25,
    "Utilização Médico cirurgião geral": 26,
    "Utilização Médico clínico geral": 27,
    "Utilização Médico gineco obst.": 28,
    "Utilização Médico pediatra": 29,

    "Cadastro Estabelecimentos Urgência": 30,
    "Contratação Estabelecimentos Urgência": 31,
    "Interseção Vazios Urgência": 32,
    "Utilização Estabelecimento Urgência": 33,

    "Cadastro Estabelecimentos Internação": 16,
    "Contratação Estabelecimentos Internação": 17,
    "Interseção Vazios Internação": 18,
    "Utilização Estabelecimento Internação": 19,

    "Cadastro Serviço Quimioterapia": 9,
    "Cadastro Serviço Radioterapia": 10,
    "Cadastro Serviço Hemodiálise": 4,
    "Utilização Serviço Quimioterapia": 14,
    "Utilização Serviço Radioterapia": 15,
    "Utilização Serviço Hemodiálise": 13,
    "Interseção dos vazios Alta Complexidade": 12,
    "Contratação Serviço Alta Complexidade": 11
  };

  return mapaIndices[especialidade] ?? 4;
};

const Gaps = () => {
  const [geoData, setGeoData] = useState(null);
  const [colors, setColors] = useState({});
  const [center, setCenter] = useState([-55, -15]);
  const [scale, setScale] = useState(900);
  const [selectedUF, setSelectedUF] = useState("AC");
  const [allVazios, setAllVazios] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categoria, setCategoria] = useState("");
  const [especialidade, setEspecialidade] = useState("");

  const [open, setOpen] = useState(false)

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    DimensionamentoService.getAssistenceGaps().then(setAllVazios).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchGeoAndFilter = async (uf) => {
      if (!especialidade) return;
      setLoading(true);
      try {
        const geoJson = await fetch(`/jsons/geojs-${uf}.json`).then(res => res.json());
        if (geoJson.centroid?.coordinates) {
          setCenter(geoJson.centroid.coordinates);
          setScale(getAutoScale(geoJson));
        } else {
          setCenter([-55, -15]);
          setScale(isMobile ? 400 : 800);
        }

        const indexDisponivel = getDisponibilidadeIndex(especialidade);
        const cores = {};
        const listaMunicipios = [];
        const municipiosSet = new Set();

        if (allVazios.length > 1) {
          allVazios.slice(1).forEach((linha) => {
            if (!linha || linha.length <= indexDisponivel) return;
            const ufline = linha[0]?.toString().trim();
            const municipio = linha[3]?.toString().trim();
            const disponivel = linha[indexDisponivel]?.toString().trim() === "True";

            if (!ufline || !municipio) return;

            cores[municipio] = disponivel ? "#007b49" : "#ff0000";

            if (ufline === uf) {
              const normalize = (str) =>
                str?.toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ");

              const municipioKeyCheck = `${normalize(ufline)}-${normalize(municipio)}`;

              if (!municipiosSet.has(municipioKeyCheck)) {
                municipiosSet.add(municipioKeyCheck);
                listaMunicipios.push({
                  uf: ufline,
                  name: municipio,
                  disponivel,
                  id: `${ufline}-${municipio}`
                });
              }
            }
          });
        }

        setGeoData(geoJson);
        setColors(cores);
        setMunicipios(listaMunicipios);
      } catch (error) {
        console.error("Erro ao buscar dados ou carregar mapa:", error);
        setGeoData(null);
        setColors({});
        setMunicipios([]);
      } finally {
        setLoading(false);
      }
    };

    if (especialidade && selectedUF && allVazios.length > 0) {
      setGeoData(null);
      fetchGeoAndFilter(selectedUF);
    }
  }, [selectedUF, allVazios, isMobile, especialidade]);

  const getAutoScale = (geoJson) => {
    if (!geoJson?.features?.length) return 1000;
    let [minX, maxX, minY, maxY] = [Infinity, -Infinity, Infinity, -Infinity];

    geoJson.features.forEach((f) => {
      const coords = f.geometry.coordinates.flat(Infinity);
      for (let i = 0; i < coords.length; i += 2) {
        const lon = coords[i];
        const lat = coords[i + 1];
        if (lon < minX) minX = lon;
        if (lon > maxX) maxX = lon;
        if (lat < minY) minY = lat;
        if (lat > maxY) maxY = lat;
      }
    });

    const width = maxX - minX;
    const height = maxY - minY;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    const factor = isMobile ? 100 : 20;

    const scaleX = (viewportWidth / width) * factor;
    const scaleY = (viewportHeight / height) * factor;

    return Math.max(800, Math.min(3500, Math.min(scaleX, scaleY)));
  };

  return (
    <div className="flex flex-col items-center justify-center w-full overflow-hidden bg-[#fff] text-secondary h-full mx-4">
      <h1 className={`font-bold text-emerald-700 mb-6 mt-2 ${isMobile ? "text-lg" : "text-3xl"}`}>Vazios Assistenciais</h1>
      <div className="flex flex-col items-center justify-start w-full bg-white text-secondary border border-gray-200 shadow-md rounded-lg p-6 max-w-[1200px] h-full mx-auto overflow-hidden">
        <Tooltip className="z-50" id="map-tooltip"/>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          justifyContent="center"
          mb={4}
          sx={{
            width: '100%',
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >

          <FormControl
            size="small"
            sx={{
              width: {xs: "100%", sm: 250},
              backgroundColor: 'white'
            }}
          >
            <InputLabel>Tipo</InputLabel>
            <Select
              variant="outlined"
              value={categoria}
              label="Tipo"
              onChange={(e) => {
                setCategoria(e.target.value);
                setEspecialidade("");
              }}
            >
              <MenuItem value=""><em>Selecione...</em></MenuItem>
              {Object.keys(categorias).map((cat) => (
                <MenuItem key={cat}
                          value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: {xs: "100%", sm: 250},
              backgroundColor: 'white'
            }}
            disabled={!categoria}
          >
            <InputLabel>Especialidade</InputLabel>
            <Select
              variant="outlined"
              value={especialidade}
              label="Especialidade"
              onChange={(e) => setEspecialidade(e.target.value)}
            >
              <MenuItem
                value=""><em>Selecione...</em></MenuItem>
              {categoria && categorias[categoria].map((opt) => (
                <MenuItem key={opt}
                          value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton
            onClick={() => setOpen(true)}
            style={{
              backgroundColor: "#007b49",
              color: "white",
              maxWidth: '40px',
              height: '40px'
            }}
            sx={{
              '&:hover': {
                backgroundColor: "#005e3f",
              },
              borderRadius: 2,
            }}
            aria-label="Abrir tabela de municípios"
          >
            <TableChartIcon />
          </IconButton>
        </Box>


        {especialidade && (
          <div className="w-full max-w-[90vw] overflow-x-auto mb-6 mt-2">
            <Tabs
              value={selectedUF}
              onChange={(e, newUF) => setSelectedUF(newUF)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{style: {backgroundColor: "#007b49"}}}
            >
              {ufs.map((uf) => (
                <Tab
                  key={uf}
                  label={uf}
                  value={uf}
                  style={{
                    minWidth: isMobile ? 60 : 80,
                    fontSize: isMobile ? "0.75rem" : "1rem",
                    textTransform: "none",
                  }}
                  sx={{
                    color: "#333",
                    '&.Mui-selected': {
                      color: "#007b49",
                      fontWeight: 600,
                    },
                  }}
                />
              ))}
            </Tabs>
          </div>
        )}

        {!especialidade ? (
          <p className="text-center text-gray-500 italic mt-4 text-sm sm:text-base">
            Selecione uma especialidade para visualizar os dados.
          </p>
        ) : loading ? (
          <p className="text-lg text-gray-700 font-medium">Carregando mapa...</p>
        ) : geoData ? (
          <>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{scale}}
              style={{
                width: "100%",
                height: isMobile ? "55vh" : "65vh",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <ZoomableGroup center={center}>
                <Geographies geography={geoData}>
                  {({geographies}) =>
                    geographies.map((geo) => {
                      const cityName = geo.properties.name;
                      const fillColor = colors[cityName] || "#D6D6DA";
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          style={{
                            default: {
                              fill: fillColor,
                              outline: "none",
                              stroke: "#FFFFFF",
                              strokeWidth: 0.3
                            },
                            hover: {
                              fill: "#005538",
                              outline: "none",
                              stroke: "#FFFFFF",
                              strokeWidth: 0.3
                            },
                            pressed: {
                              fill: "#E42",
                              outline: "none",
                              stroke: "#FFFFFF",
                              strokeWidth: 0.3
                            },
                          }}
                          data-tooltip-id="map-tooltip"
                          data-tooltip-content={geo.properties.name}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            <div className="mt-6 flex gap-6 flex-wrap justify-center items-center text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-[#007b49] rounded-sm border border-gray-300"/>
                <span className="text-sm">Serviço disponível: Sim</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-[#ff0000] rounded-sm border border-gray-300"/>
                <span className="text-sm">Serviço disponível: Não</span>
              </div>
            </div>

            <TableUF key={selectedUF}
                     selectedUF={selectedUF}
                     municipios={municipios}
                     open={open}
                     setOpen={setOpen}
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Gaps;
